import { Mood, SecondaryMood } from '@peekapak/types/Mood';
import { GradeBand } from '@peekapak/types/EmotionsClassification';
import { EmotionsReportCheckin } from '@peekapak/types/EmotionsReportRow';
import { resolveSixToTwelveCategory } from './ConcerningEmotionsReportUtilities';

type Feeling = Mood | SecondaryMood;

// 6-12 sub-feelings roll up to their category, matching the School Wide Moodboard's 6-12 chart.
function toChartFeeling(feeling: Feeling, gradeBand: GradeBand): Feeling {
  if (gradeBand === 'PK-5') return feeling;
  // PK-5 "Excited" has no 6-12 category; count it under Happy.
  if (feeling === Mood.EXCITED) return Mood.HAPPY;
  return resolveSixToTwelveCategory(feeling) ?? feeling;
}

function formatShortDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export interface WeekBucket {
  weekLabel: string;
  counts: Partial<Record<Feeling, number>>;
  dates: Partial<Record<Feeling, string[]>>;
}

// Weeks are day-of-month buckets (1-7, 8-14, 15-21, 22-end), not calendar weeks.
export function buildWeeklyChartData(
  checkins: EmotionsReportCheckin[],
  year: number,
  month: number,
  gradeBand: GradeBand,
): WeekBucket[] {
  const monthStart = new Date(year, month, 1).getTime();
  const monthEnd = new Date(year, month + 1, 1).getTime();
  const inMonth = checkins.filter((checkin) => checkin.createdAt >= monthStart && checkin.createdAt < monthEnd);

  const weeks: WeekBucket[] = [1, 2, 3, 4].map((weekNumber) => ({
    weekLabel: `Week ${weekNumber}`,
    counts: {},
    dates: {},
  }));

  for (const checkin of inMonth) {
    const dayOfMonth = new Date(checkin.createdAt).getDate();
    const weekIndex = Math.min(3, Math.floor((dayOfMonth - 1) / 7));
    const feeling = toChartFeeling(checkin.feeling, gradeBand);
    const bucket = weeks[weekIndex];
    bucket.counts[feeling] = (bucket.counts[feeling] ?? 0) + 1;
    bucket.dates[feeling] = [...(bucket.dates[feeling] ?? []), formatShortDate(checkin.createdAt)];
  }

  return weeks;
}

// Same order as the School Wide Moodboard's PK-5 "This Period" tiles.
const MOODBOARD_ORDER = [Mood.EXCITED, Mood.HAPPY, Mood.CALM, Mood.NERVOUS, Mood.SAD, Mood.ANGRY] as const;

// Same order as the School Wide Moodboard's 6-12 "This Period" tiles.
const MOODBOARD_ORDER_6_12 = [
  Mood.HAPPY,
  Mood.CONFIDENT,
  Mood.SURPRISED,
  Mood.CALM,
  Mood.SAD,
  Mood.SCARED,
  Mood.ANGRY,
] as const;

export interface PeriodPercentage {
  feeling: Feeling;
  percentage: number;
}

// Percentages round independently per tile, so they may not sum to exactly 100%.
// Only feelings that actually occurred get a tile - no fixed grade-band palette.
export function buildPeriodPercentages(
  checkins: EmotionsReportCheckin[],
  year: number,
  month: number,
  gradeBand: GradeBand,
): PeriodPercentage[] {
  const monthStart = new Date(year, month, 1).getTime();
  const monthEnd = new Date(year, month + 1, 1).getTime();
  const inMonth = checkins.filter((checkin) => checkin.createdAt >= monthStart && checkin.createdAt < monthEnd);

  const total = inMonth.length;
  const counts = new Map<Feeling, number>();

  for (const checkin of inMonth) {
    const feeling = toChartFeeling(checkin.feeling, gradeBand);
    counts.set(feeling, (counts.get(feeling) ?? 0) + 1);
  }

  const moodOrder = (feeling: Feeling) =>
    ((gradeBand === 'PK-5' ? MOODBOARD_ORDER : MOODBOARD_ORDER_6_12) as readonly string[]).indexOf(feeling);

  return [...counts.entries()]
    .sort(([feelingA, a], [feelingB, b]) => moodOrder(feelingA) - moodOrder(feelingB) || b - a)
    .map(([feeling, count]) => ({
      feeling,
      percentage: Math.round((count / total) * 100),
    }));
}

export function buildRecentCheckins(checkins: EmotionsReportCheckin[]): EmotionsReportCheckin[] {
  return [...checkins].sort((a, b) => b.createdAt - a.createdAt);
}
